import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/lib/api";
import { Ticket, Check, X } from "lucide-react";

/**
 * Compact promo code entry field. Emits the applied code (uppercase) via
 * `onApplied(code)` whenever a valid code is confirmed by the backend, and
 * `onApplied("")` when the user clears/invalidates the field.
 *
 * Props:
 *   context: "signup" | "membership" | "deal"
 *   value / onApplied: controlled surface for the applied code
 *   testidPrefix: unique test-id prefix
 *
 * When `value` is passed non-empty (e.g. a promo carried over from
 * signup), it's auto-validated against the backend on mount rather than
 * trusted blindly — an expired/deactivated code should surface as invalid,
 * not silently show as applied.
 */
export default function PromoCodeInput({
    context = "deal",
    value = "",
    onApplied,
    testidPrefix = "checkout-promo",
    className = "",
}) {
    const [input, setInput] = useState(value);
    const [status, setStatus] = useState({ state: "idle" });

    const apply = async (explicitCode) => {
        const code = (typeof explicitCode === "string" ? explicitCode : input).trim();
        if (!code) {
            setStatus({ state: "idle" });
            onApplied?.("");
            return;
        }
        setStatus({ state: "checking" });
        try {
            const { data } = await api.post("/promo-codes/validate", {
                code,
                context,
            });
            setStatus({ state: "valid", promo: data });
            onApplied?.(data.code);
        } catch (e) {
            setStatus({
                state: "invalid",
                message: formatApiError(e) || "Promo code is not valid.",
            });
            onApplied?.("");
        }
    };

    // Auto-validate a prefilled value once on mount (e.g. the promo code
    // used at signup, carried over into the first membership checkout).
    useEffect(() => {
        if (value) apply(value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const clear = () => {
        setInput("");
        setStatus({ state: "idle" });
        onApplied?.("");
    };

    return (
        <div className={className} data-testid={`${testidPrefix}-wrap`}>
            <div className="flex gap-2 items-center">
                <div className="relative flex-1">
                    <Ticket
                        size={14}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-400"
                    />
                    <Input
                        data-testid={`${testidPrefix}-input`}
                        value={input}
                        onChange={(e) => {
                            setInput(e.target.value.toUpperCase());
                            setStatus({ state: "idle" });
                        }}
                        onBlur={apply}
                        placeholder="Promo code (optional)"
                        className="h-10 pl-8 uppercase tracking-wide bg-white border-navy-100"
                    />
                </div>
                {status.state === "valid" ? (
                    <button
                        type="button"
                        onClick={clear}
                        data-testid={`${testidPrefix}-clear`}
                        className="h-10 px-3 rounded-md border border-navy-100 bg-white text-navy-500 text-xs hover:bg-navy-50"
                    >
                        Remove
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={apply}
                        data-testid={`${testidPrefix}-apply`}
                        className="h-10 px-4 rounded-md border border-navy-700 bg-white text-navy-700 text-sm font-semibold hover:bg-navy-50"
                    >
                        Apply
                    </button>
                )}
            </div>
            {status.state === "checking" && (
                <div className="text-xs text-navy-500 mt-1">Checking…</div>
            )}
            {status.state === "valid" && (
                <div
                    data-testid={`${testidPrefix}-valid`}
                    className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1 mt-2 inline-flex items-center gap-1.5"
                >
                    <Check size={12} />
                    <span>
                        <span className="font-semibold">{status.promo.code}</span> —{" "}
                        {status.promo.discount_type === "percent"
                            ? `${status.promo.discount_value}% off`
                            : `HK$${status.promo.discount_value} off`}
                    </span>
                </div>
            )}
            {status.state === "invalid" && (
                <div
                    data-testid={`${testidPrefix}-invalid`}
                    className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-2 py-1 mt-2 inline-flex items-center gap-1.5"
                >
                    <X size={12} />
                    <span>{status.message}</span>
                </div>
            )}
        </div>
    );
}
